
import { CommunityModel } from './CommunityModel';
import { CouponModel } from './CouponModel';
import { CommunityType, VendorDiscountType, ProductTaxType, DisabledType, PackageSearchType, CurrencyType } from './Enums';
import { EventModel } from './EventModel';
import { GiftcardModel } from './GiftcardModel';
import { IBenefits } from './Interfaces';
import { ProductModel } from './ProductModel';
import { ScratchModel } from './ScratchModel';
import { PagingModel } from './SearchModel';
import { VendorBasicModel } from './VendorModel';
import { CurrencyInfo } from '../currency-info';

export class PackageModel {

    constructor(init?: Partial<PackageModel>) {
        if (init) {
            Object.assign(this, init);
            if (init.vendor) {
                init.vendor = new VendorBasicModel(init.vendor);
            }

            if (init.community) {
                init.community = new CommunityModel(init.community);
            }

            if (typeof init.createdAt === 'string') {
                this.createdAt = new Date(init.createdAt);
            }

            if (init.updatedAt && typeof init.updatedAt === 'string') {
                this.updatedAt = new Date(init.updatedAt);
            }

            if (init.deletedAt && typeof init.deletedAt === 'string') {
                this.deletedAt = new Date(init.deletedAt);
            }

            if (typeof init.saleDateStartAt === 'string') {
                this.saleDateStartAt = new Date(init.saleDateStartAt);
            }

            if (typeof init.saleDateEndAt === 'string') {
                this.saleDateEndAt = new Date(init.saleDateEndAt);
            }

            if (init.expirationDateAt && typeof init.expirationDateAt === 'string') {
                this.expirationDateAt = new Date(init.expirationDateAt);
            }

            if(init.imageUrl != null) this.thumbnailUrl = init.imageUrl;
        }
    }

    id: number;
    vendorId: number;
    appType: CommunityType;

    name: string;
    description: string;
    terms: string;
    instructions: string;

    bannerUrl: string;
    imageUrl: string;
    colorTheme: string;

    vendorPrice: number;
    vendorPriceWithTax: number;

    vendorDiscountType: VendorDiscountType;
    vendorPriceWithDiscount: number;

    price: number;

    costTransaction = 350;

    commissionCost: number;
    validationCost: number;
    isCommissionToVendor: boolean;

    priceWithDiscount: number;
    plusPrice: number;

    allowDiscount: boolean;
    discountPercent: number;

    taxType: ProductTaxType;
    taxPercent: number;

    stock: number;
    purchasesCount: number;
    quantity: number;
    purchaseLimit: number;

    saleDateStartAt: Date;
    saleDateEndAt: Date;
    expirationDateAt?: Date;

    isAvailable: boolean;
    showInApp: boolean;

    createdAt: Date;
    updatedAt?: Date;
    deletedAt?: Date;
    approvedAt: Date;

    vendorCommissionPercent: number = 0;
    customerCommissionPercent: number = 0;

    thumbnailUrl: string;
    
    communityId: number;

    //interface, we filled it into _fillBenefits in package details
    packageBenefits: IBenefits[] = [];

    community: CommunityModel;

    vendor: VendorBasicModel;
    coupons: CouponModel[] = [];
    giftcards: GiftcardModel[] = [];
    products: ProductModel[] = [];
    events: EventModel[] = [];
    cashless: EventModel[] = [];
    scratches: ScratchModel[] = [];
    vendors: PackageVendorModel[] = [];
    
    get currencySymbol(): string {

        return CurrencyInfo.symbol(this.vendor?.currencyType ?? CurrencyType.CRC);
    }

    // Used to display in the form invoice
    get customerCommission(): number {
        let commission = this.customerCommissionPercent > 0 ? (this.price * (this.customerCommissionPercent / 100)) : 0;
        return commission;
    }
}

export class PackageVendorModel {

    constructor(init?: Partial<PackageVendorModel>) {
        if (init) {
            Object.assign(this, init);

            if (typeof init.requestedAt === 'string') {
                this.requestedAt = new Date(init.requestedAt);
            }

            if (init.approvedAt && typeof init.approvedAt === 'string') {
                this.approvedAt = new Date(init.approvedAt);
            }

            if (init.rejectedAt && typeof init.rejectedAt === 'string') {
                this.rejectedAt = new Date(init.rejectedAt);
            }

            if (typeof init.createdAt === 'string') {
                this.createdAt = new Date(init.createdAt);
            }

            if (init.updatedAt && typeof init.updatedAt === 'string') {
                this.updatedAt = new Date(init.updatedAt);
            }

            if (init.deletedAt && typeof init.deletedAt === 'string') {
                this.deletedAt = new Date(init.deletedAt);
            }

            if (init.disabledAt && typeof init.disabledAt === 'string') {
                this.disabledAt = new Date(init.disabledAt);
            }
        }
    }

    id: number;
    packageId: number;
    vendorId: number;

    requestCode: string;
    requestedAt: Date;
    approvedAt?: Date;
    rejectedAt?: Date;

    contactName: string;
    contactPhone: string;
    contactEmail: string;

    isActive: boolean;
    disabledType: DisabledType;
    disabledAt?: Date;

    createdAt: Date;
    updatedAt?: Date;
    deletedAt?: Date;

    package: PackageModel;
    vendor: VendorBasicModel;
}

export class PackageBasicModel {

    constructor(init?: Partial<PackageBasicModel>) {
        if (init) {
            Object.assign(this, init);

            if (typeof init.createdAt === 'string') {
                this.createdAt = new Date(init.createdAt);
            }

            if (init.updatedAt && typeof init.updatedAt === 'string') {
                this.updatedAt = new Date(init.updatedAt);
            }

            if (init.deletedAt && typeof init.deletedAt === 'string') {
                this.deletedAt = new Date(init.deletedAt);
            }

            if (typeof init.saleDateStartAt === 'string') {
                this.saleDateStartAt = new Date(init.saleDateStartAt);
            }

            if (typeof init.saleDateEndAt === 'string') {
                this.saleDateEndAt = new Date(init.saleDateEndAt);
            }

            if (init.expirationDateAt && typeof init.expirationDateAt === 'string') {
                this.expirationDateAt = new Date(init.expirationDateAt);
            }
        }
    }

    id: number;
    vendorId: number;
    appType: CommunityType;

    name: string;
    description: string;
    terms: string;
    instructions: string;

    bannerUrl: string;
    imageUrl: string;
    colorTheme: string;

    vendorPrice: number;  // Precio proveedor sin IVA
    vendorPriceWithTax: number;  // Precio proveedor con IVA

    vendorDiscountType: VendorDiscountType;  // Descuento para el Precio a Givi
    vendorPriceWithDiscount: number;  // Precio a Givi

    price: number;  // Precio de venta

    commissionCost: number;  // Comisión 10% (sin impuestos)
    validationCost: number;  // Costo validación ($.50 por todas las transacciones)
    isCommissionToVendor: boolean;  // Indica si el comercio asume los costos de comisión y validación

    priceWithDiscount: number;  // Precio de venta en SALE
    plusPrice: number;  // Precio de venta por experiencia

    allowDiscount: boolean;
    discountPercent: number;

    taxType: ProductTaxType;
    taxPercent: number;

    stock: number;  // Cantidad de paquetes disponibles (0 ilimitado)
    quantity: number;  // Total vendido del inventario (si es manejado por el paquete)

    purchaseLimit: number;  // Límite de cantidad de paquetes que se pueden comprar (0 ilimitado)

    saleDateStartAt: Date;  // Fecha para comenzar la compra del paquete
    saleDateEndAt: Date;  // Fecha para finalizar la compra del paquete

    expirationDateAt?: Date;

    isAvailable: boolean;  // Define si está disponible para ser utilizado
    showInApp: boolean;  // Define si se muestra en el market del App

    createdAt: Date;
    updatedAt?: Date;
    deletedAt?: Date;
}

export class PackageSearchModel {
    type: PackageSearchType;
    paging: PagingModel;
}